import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faExternalLinkAlt, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import { providerStatusClass } from '../orderStatus';
import './Orders.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const POLL_INTERVAL = 3 * 60 * 1000;

function OrderLinkCell({ link }) {
  const [copied, setCopied] = useState(false);

  if (!link) return <span>—</span>;

  const safeUrl = link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}`;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="order-link-cell">
      <a
        href={safeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="order-link-anchor"
        title={link}
      >
        <span className="order-link-text">{link}</span>
        <FontAwesomeIcon icon={faExternalLinkAlt} className="order-link-icon" />
      </a>
      <button
        type="button"
        className={`order-link-copy-btn ${copied ? 'copied' : ''}`}
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy Link'}
        aria-label="Copy Link"
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        {copied && <span className="copied-tooltip">Copied!</span>}
      </button>
    </div>
  );
}

export default function Orders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const searchRef = useRef(search);
  const statusRef = useRef(statusFilter);

  searchRef.current = search;
  statusRef.current = statusFilter;

  const fetchOrders = useCallback(() => {
    const params = new URLSearchParams();
    if (statusRef.current) params.set('status', statusRef.current);
    if (searchRef.current) params.set('search', searchRef.current);

    fetch(`${API}/orders?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
        setLastUpdated(new Date());
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  useEffect(() => {
    if (!search) return;
    const timer = setTimeout(fetchOrders, 300);
    return () => clearTimeout(timer);
  }, [search, fetchOrders]);

  return (
    <div className="orders-page">
      <div className="orders-filters">
        {lastUpdated && (
          <span className="orders-last-updated">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <div className="search-bar">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by ID, service or platform..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="processing">In Progress</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Service</th>
              <th>Platform</th>
              <th>Category</th>
              <th>Link</th>
              <th>Quantity</th>
              <th>Charge</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td className="order-id">#{order.id}</td>
                <td>{order.service?.name || '—'}</td>
                <td>
                  <span className="platform-badge">{order.service?.platform || '—'}</span>
                </td>
                <td>
                  <span className="category-badge">{order.service?.category || '—'}</span>
                </td>
                <td className="order-link-td">
                  <OrderLinkCell link={order.link} />
                </td>
                <td>{Number(order.quantity).toLocaleString()}</td>
                <td>TSHS {Number(order.charge).toLocaleString()}</td>
                <td>
                  <span className={`status-badge ${providerStatusClass(order.provider_status || order.status)}`}>
                    {order.provider_status || order.status}
                  </span>
                </td>
                <td className="order-date">{new Date(order.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={9} className="orders-empty">No orders found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

